import React, { CSSProperties } from 'react';
import {
  Alert,
  Card,
  Flex,
  Space,
  Steps,
  Tag,
  Typography,
} from '@duxton-web/ui';

import { mapVariableCanaryToConfig } from '@/features/variable/settings/canary';
import { isCanaryEligible } from '@/features/variable/settings/canary/eligibility';
import { DEFAULT_CANARY_PHASES } from '@/features/variable/settings/canary/phase-utils';
import { VariableInfo } from '@/lib/PlannerDTOs';

export interface CanaryRolloutPlanPhase {
  kind: 'phase';
  label: string;
  targetPercentage: number;
  durationMins: number;
}

export interface CanaryRolloutPlanFinalState {
  kind: 'final';
  label: 'Final release';
  targetPercentage: 100;
}

export type CanaryRolloutPlanItem =
  | CanaryRolloutPlanPhase
  | CanaryRolloutPlanFinalState;

const getConfiguredPhases = (
  variable: VariableInfo,
): CanaryRolloutPlanPhase[] | undefined => {
  const hasExistingCanaryConfig =
    variable.metadata?.canaryConfig != null ||
    variable.canary?.enabled === true;
  if (!hasExistingCanaryConfig && !isCanaryEligible(variable)) return undefined;

  const normalizedConfig = mapVariableCanaryToConfig(variable);
  if (!normalizedConfig.enabled) return undefined;

  return normalizedConfig.phases.map((phase, index) => ({
    kind: 'phase' as const,
    label: `Phase ${index + 1}`,
    targetPercentage: phase.targetPercentage,
    durationMins: phase.durationMins,
  }));
};

export const buildCanaryRolloutPlan = (
  variable?: VariableInfo,
): CanaryRolloutPlanItem[] => {
  if (!variable) return [];

  const configuredPlanPhases = getConfiguredPhases(variable);
  if (!configuredPlanPhases) return [];

  const phases =
    configuredPlanPhases.length > 0
      ? configuredPlanPhases
      : DEFAULT_CANARY_PHASES.map((phase, index) => ({
          kind: 'phase' as const,
          label: `Phase ${index + 1}`,
          ...phase,
        }));
  const endsAtFullRelease = phases[phases.length - 1]?.targetPercentage === 100;
  const observationPhases = endsAtFullRelease ? phases.slice(0, -1) : phases;

  return [
    ...observationPhases,
    {
      kind: 'final' as const,
      label: 'Final release' as const,
      targetPercentage: 100 as const,
    },
  ];
};

interface CanaryRolloutPlanSummaryProps {
  variable?: VariableInfo;
  style?: CSSProperties;
}

const CanaryRolloutPlanSummary: React.FC<CanaryRolloutPlanSummaryProps> = ({
  variable,
  style,
}) => {
  const plan = buildCanaryRolloutPlan(variable);

  if (plan.length === 0) return null;

  return (
    <Card
      data-testid="canary-rollout-plan-summary"
      size="small"
      style={style}
      title="Canary rollout plan"
    >
      <Flex vertical gap={12}>
        <Typography.Text type="secondary">
          Review the gradual exposure before requesting approval.
        </Typography.Text>

        <section aria-label="Canary rollout plan">
          <Steps
            responsive
            size="small"
            items={plan.map((item) => ({
              status: 'wait',
              title: (
                <Typography.Text strong>
                  {item.label} — {item.targetPercentage}%
                </Typography.Text>
              ),
              description:
                item.kind === 'phase' ? (
                  <Typography.Text type="secondary">
                    {item.durationMins} min observation
                  </Typography.Text>
                ) : (
                  <Space size={4} wrap>
                    <Tag size="xsmall" color="success">
                      After promotion
                    </Tag>
                    <Tag size="xsmall">Fixed final state</Tag>
                  </Space>
                ),
            }))}
          />
        </section>

        <Alert
          type="info"
          showIcon
          message="Manual promotion required"
          description="After the last configured Canary phase completes, a user must click Promote to release the change to 100%."
        />
      </Flex>
    </Card>
  );
};

export default CanaryRolloutPlanSummary;
